<template>
    <div>
        <!-- Dektop -->
        <div
            class="flex justify-between sm:justify-end md:justify-end items-end sm:!hidden md:!hidden"
        >
            <ul class="game-info-tabs">
                <li
                    v-for="(tab, idx) of allTabs"
                    :key="idx"
                    :class="{ active: tab.value == modelValue }"
                    @click="handleClick(tab.value)"
                >
                    <IconSvgUse size="24px" :use="tab.iconName" />
                    <span>&nbsp;{{ t[tab.tabLangKey] }}</span>
                    <!--
                        ↑ 通用内容
                        ↓ 特定游戏的内容
                    -->
                    <span 
                        class="data-length" 
                        :class="tab.value == modelValue && 'avtive-length'"
                        v-if="tab.name === 'betsOnline'"
                    >
                        {{ betsOnlineNum }}
                    </span>
                    <span class="data-length avtive-length" 
                        v-if="(
                            tab.value === HistoryTabsEnum.active 
                            && iWavesCount > 0
                        )"
                    >
                        {{ iWavesCount }}
                    </span>
                </li>
            </ul>
            <!-- ↓ 选择每页的行数 -->
            <DropDown class="!py-1 h-[50%] sm:hidden md:hidden"
                :model-value="sSizePicker"
                @update:model-value="emit('update:sSizePicker', $event)"
                :options="aPagination"
            >
            </DropDown>
        </div>
        <!-- Mobile -->
        <div class="flex justify-between items-center lg:hidden xl:hidden">
            <DropDown
                v-if="!mobileActive"
                class="py-2 px-3 lg:hidden xl:hidden"
                :model-value="allTabsDropDown.value"
                @update:model-value="emit('update:modelValue', $event)"
                :options="allTabsDropDown.options"
            >
                <template v-slot:header>
                    <IconSvgUse
                        class="w-[20px] h-[20px] text-[#55657e]"
                        v-if="selectedTab?.iconName"
                        :use="selectedTab?.iconName"
                    />
                    <span class="font-semibold">{{ t[selectedTab?.tabLangKey] }}</span>
                </template>
                <DropDownItem
                    class="gap-2 flex text-xs cursor-pointer"
                    :current-value="allTabsDropDown.value"
                    @click="handleClick(tab.value)"
                    v-for="(tab, idx) of allTabs"
                    :key="idx"
                    :value="tab.value"
                >
                    <IconSvgUse class="w-[20px] h-[20px]" :use="tab.iconName" />
                    <span class="ellipsis flex-1">{{ t[tab.tabLangKey] }}</span>
                </DropDownItem>
            </DropDown>
            <!-- ↓ 切换到 Waves 游戏的排行榜时，下方出现一个“币种选择菜单” -->
            <wavesCoinPicker
                v-if="modelValue === HistoryTabsEnum.leaderboard" 
                class='w-full mt-2'
            ></wavesCoinPicker>
            <CasinoWidgetRTP
                v-else-if="gameName != 'futuresGame'"
                type="old"
                class="!m-0"
            />
        </div>
    </div>
</template>

<script lang="ts">
export default defineComponent({ name: 'TabsAboveBigTables' });
</script>

<script setup lang="ts">
import wavesCoinPicker from './wavesCoinPicker.vue';
import useTranslation from '~/i18n/useTransLation';
import { useJoinStateStore } from '@/stores/cansino/joinState';
import { useUserStore } from '~/stores/user';
import {HistoryTabsEnum, aPagination} from '@/components/Casino/History/Tabs/ts/Tabs';
import {
    oPositionData_i, 
    oElementVisible, 
} from '@/pages/casino/futuresGame/ts/wavesControlCenter'; 

const t = useTranslation();
const userStore = useUserStore();
const oJoinStateStore = useJoinStateStore();
const emit = defineEmits([
    'update:modelValue',
    'update:sSizePicker',
]);

const props = defineProps({
    isMobileMode: Boolean,
    items: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: String,
        default: '',
        required: true,
    },
    sSizePicker: {
        type: String,
        default: '10',
        required: true,
    },
    gameName: {
        type: String,
    },
    mobileActive: { // Waves 游戏专用 
        type: Boolean,
    },
});

const toRefModalValue = toRef(props, 'modelValue');

const allTabs = computed(()=>{
    const gameOfRoute = oJoinStateStore.gameOfRoute;
    if (gameOfRoute === 'futuresGame') {
        // console.log("waves 游戏进行中"); 
        const aWavesOnly = [{
            name: 'Active', // 这个属性似乎没用上，观察中
            tabLangKey: 'result_tab.active', // i18n 的 key 
            iconName: 'icon-user2', // 图标名 
            value: HistoryTabsEnum.active,
            show: !oElementVisible.value.showTopActiveTable,
        },{
            name: 'Closed', 
            tabLangKey: 'result_tab.closed', 
            iconName: 'icon-my-bets', 
            value: HistoryTabsEnum.closed,
            show: true,
        },{
            name: 'Public', 
            tabLangKey: 'result_tab.public', 
            iconName: 'icon-referral-2', 
            value: HistoryTabsEnum.public,
            show: true,
        },{
            name: 'Leaderboard', 
            tabLangKey: 'result_tab.leaderboard', 
            iconName: 'icon-rating', 
            value: HistoryTabsEnum.leaderboard,
            show: true,
        },].filter(cur => { 
            return cur.show; 
        });
        return aWavesOnly; 
    }
    const aResult = [
        {
            name: 'betsOnline',
            tabLangKey: 'result_tab.bets_online',
            iconName: 'icon-shows',
            value: HistoryTabsEnum.BetsOnline,
            show: ['circleGame'].includes(gameOfRoute),
        },
        {
            name: 'allBets',
            tabLangKey: 'result_tab.all_bets',
            iconName: 'icon-all-bets',
            value: HistoryTabsEnum.All,
            show: !['circleGame', 'crashGame'].includes(gameOfRoute),
        },
        {
            name: 'myBets',
            tabLangKey: 'result_tab.my_bets',
            iconName: 'icon-table-user',
            value: HistoryTabsEnum.MyBets,
            show: userStore.isLoggedIn, // 登录后才显示 
        },
        {
            name: 'history',
            tabLangKey: 'result_tab.history',
            iconName: 'icon-my-bets',
            value: HistoryTabsEnum.History,
            show: ['circleGame', 'crashGame'].includes(gameOfRoute),
        },
        {
            name: 'highRollers',
            tabLangKey: 'result_tab.high_rollers',
            iconName: 'icon-high-rollers',
            value: HistoryTabsEnum.High,
            show: true
        },
        {
            name: 'rareWins',
            tabLangKey: 'result_tab.rare_wins',
            iconName: 'icon-rare-wins',
            value: HistoryTabsEnum.Rare,
            show: true
        },
    ].filter(cur=>{
        return cur.show;
    });
    // ↓ 特定的游戏要重新排序
    if (gameOfRoute === 'crashGame'){
        const aOrder = ['history', 'myBets', 'highRollers', 'rareWins'];
        aResult.sort((aa, bb) => {
            const idx01 = aOrder.indexOf(aa.name);
            const idx02 = aOrder.indexOf(bb.name);
            return idx01 - idx02;
        });
    }
    return aResult;
});


// ↓ 被选中的项
const selectedTab = computed(() =>{
    const oResult = allTabs.value.find((tab) => {
        return tab.value === allTabsDropDown.value;
    });
    return oResult;
});


// 计算实时下注数据数量 circle 游戏专用 
const betsOnlineNum = computed(() => {
    let {length} = oJoinStateStore.betsOnlineData || [];
    // ↓ 原站点时而显示 "00"，不合适。应显示为 "0"  
    if (!length) return '0'; 
    const sQuantity = String(length).padStart(2, '0');
    return sQuantity;
});


// waves 游戏的 active 合约数量 
const iWavesCount = computed(() => {
    let iResult = 0;
    if (oPositionData_i.currentTab == HistoryTabsEnum.active){
        iResult = (
            oPositionData_i.mobTotals
            || oPositionData_i.totals
        );
    }
    return iResult;
});

// ↓ 用于移动端下拉菜单 
const allTabsDropDown = reactive({
    value: toRefModalValue,
    options: allTabs.value,
});


function handleClick(sName: string) {
    emit('update:modelValue', sName);
    emit('update:sSizePicker', '10')
}

</script>


<style scoped lang="scss" src="./style/Tab.scss"></style>
<style lang="scss" scoped>
.data-length {
    background-color: #2283f6;
    border-radius: 6px;
    color: #fff;
    font-feature-settings: "tnum" on,"lnum" on;
    font-size: 10px;
    font-weight: 700;
    height: 18px;
    margin-left: 8px;
    padding: 0 6px;
    display: inline-block;
    line-height: 18px;
}
.avtive-length {
    background-color: #fff;
    color: #2283f6;
}
</style>
